import React from 'react'

import { CircleItemBulletPoint } from 'components/reusable/CircleItemBulletPoint'
import { TextLink } from 'slab'
import { calculateAgeAtDate, getFirstName } from 'utils'
import { type MemberOnboardingMedicalPlanDisplayItem, type QuotingQuoteResult } from 'utils/web_api.schemas'

import { EmployeePlanCard } from './EmployeePlanCard'
import { EmployeePlanFilters, type PlanFiltersState } from './EmployeePlanFilters'
import { EmployeePlanSummary } from './EmployeePlanSummary'

interface PlansTabContentProps {
  employee: QuotingQuoteResult
  plans: MemberOnboardingMedicalPlanDisplayItem[] | undefined
  plansLoading: boolean
  hasPlansError: boolean
  filters: PlanFiltersState
  setFilters: (filters: PlanFiltersState) => void
  filteredPlans: MemberOnboardingMedicalPlanDisplayItem[]
  monthlyAllowance: number
  usingProjectedData: boolean
  quoteYear: number | null
}

export const PlansTabContent = ({
  employee,
  plans,
  plansLoading,
  hasPlansError,
  filters,
  setFilters,
  filteredPlans,
  monthlyAllowance,
  usingProjectedData,
  quoteYear,
}: PlansTabContentProps) => {
  const employeeAge = calculateAgeAtDate(employee.date_of_birth, new Date())
  const isMedicareEligible = employeeAge >= 65
  const employeeName = getFirstName(employee.name) || employee.name

  const plansCountText = usingProjectedData
    ? `${filteredPlans.length} example medical plans for`
    : `${filteredPlans.length} medical plans for`

  // Sort plans: best fit first, then by cost to employee (descending)
  const sortedPlans = React.useMemo(() => {
    if (!filteredPlans || filteredPlans.length === 0) return []

    return [...filteredPlans].sort((a, b) => {
      const aPremium = (a.total_premium.subunits / 100)
      const bPremium = (b.total_premium.subunits / 100)
      const aCostToEmployee = monthlyAllowance - aPremium
      const bCostToEmployee = monthlyAllowance - bPremium

      // Check if either plan is the employee's current medical plan (best fit)
      const aIsBestFit = employee.medical_plan?.id === a.plan.id
      const bIsBestFit = employee.medical_plan?.id === b.plan.id

      // Best fit plan goes first
      if (aIsBestFit && !bIsBestFit) return -1
      if (!aIsBestFit && bIsBestFit) return 1

      // If both are best fit or neither are, sort by cost to employee (descending)
      return bCostToEmployee - aCostToEmployee
    })
  }, [filteredPlans, monthlyAllowance, employee.medical_plan?.id])

  return (
    <div className='space-y-4'>
      <EmployeePlanSummary
        employee={employee}
        plans={plans}
        monthlyAllowance={monthlyAllowance}
      />

      <hr className='border-gray-200' />
      {plansLoading && (
        <div className='text-center py-12'>
          <div className='text-gray-400 mb-4'>
            <svg className='mx-auto h-8 w-8 animate-spin' fill='none' viewBox='0 0 24 24'>
              <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
              <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
            </svg>
          </div>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>Loading Plans</h3>
          <p className='text-gray-500'>
            Finding available health plans for{' '}
            <span className='sensitive'>{employeeName || employee.name}</span>
            ...
          </p>
        </div>
      )}

      {hasPlansError && (
        <div className='text-center py-12'>
          <div className='text-red-400 mb-4'>
            <svg className='mx-auto h-12 w-12' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z' />
            </svg>
          </div>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>Error Loading Plans</h3>
          <p className='text-gray-500'>Unable to load available plans. Please try again later.</p>
        </div>
      )}

      {!plansLoading && !hasPlansError && isMedicareEligible && (
        <div className='mb-10'>
          <h2 className='heading-sm text-gray-800 mb-3'>
            <span className='sensitive'>{employeeName}</span> is Medicare
            eligible
          </h2>
          <p className='text-md font-semibold'>
            Here's how it'll work for{' '}
            <span className='sensitive'>{employeeName}</span>:
          </p>
          <ul className='list-none body pl-4 pt-2 mb-4'>
            <li className='mb-2'>
              <div className='flex items-start space-x-2'>
                <CircleItemBulletPoint label='1' />
                <span className='flex-shrink-1'>
                  <TextLink
                    target='_blank'
                    color='purple'
                    href='https://support.thatch.com/enroll-in-medicare'
                  >
                    <span className='sensitive'>{employeeName}</span> will find
                    the Medicare configuration
                  </TextLink>{' '}
                  for their needs
                </span>
              </div>
            </li>
            <li className='mb-2'>
              <div className='flex items-start space-x-2'>
                <CircleItemBulletPoint label='2' />
                <div className='flex flex-col'>
                  <span className='flex-shrink-1'>
                    <TextLink
                      target='_blank'
                      color='purple'
                      href='https://form.typeform.com/to/GPADGMQp'
                    >
                      <span className='sensitive'>{employeeName}</span> will
                      then need to tell us
                    </TextLink>{' '}
                    what plan(s) they enrolled in
                  </span>
                </div>
              </div>
            </li>
            <li className='mb-2'>
              <div className='flex items-start space-x-2'>
                <CircleItemBulletPoint label='3' />
                <div className='flex flex-col'>
                  <span className='flex-shrink-1'>
                    They will submit their request for reimbursement for Medicare premiums to Thatch monthly
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      )}

      {!plansLoading && !hasPlansError && !isMedicareEligible && plans && plans.length > 0 && (
        <div className='space-y-4'>
          <EmployeePlanFilters
            plans={plans}
            filters={filters}
            setFilters={setFilters}
          />

          <div>
            <p className='body-bold mb-2 mt-6'>
              {plansCountText}{' '}
              <span className='sensitive'>{employeeName}</span>
            </p>
            <p className='body-sm text-gray-500'>
              Plans available for{' '}
              <span className='sensitive'>{employeeName}</span> may vary
              depending on their home address and the time they enroll.
            </p>
          </div>

          <div className='space-y-4'>
            {sortedPlans.map((plan: MemberOnboardingMedicalPlanDisplayItem, index: number) => (
              <EmployeePlanCard
                key={plan.plan.id || index}
                plan={plan}
                isIndividual={employee.dependents.length === 0}
                employee={employee}
                usingProjectedData={usingProjectedData}
                monthlyAllowance={monthlyAllowance}
                quoteYear={quoteYear}
              />
            ))}
          </div>
        </div>
      )}

      {!plansLoading && !hasPlansError && !isMedicareEligible && (!plans || plans.length === 0) && (
        <div className='text-center py-12'>
          <div className='text-gray-400 mb-4'>
            <svg className='mx-auto h-12 w-12' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
            </svg>
          </div>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>No Plans Available</h3>
          <p className='text-gray-500 max-w-md mx-auto'>
            No health plans are currently available for{' '}
            <span className='sensitive'>{employeeName}</span> in their area.
            This may be due to their location or current plan availability.
          </p>
        </div>
      )}
    </div>
  )
}
